/**
 * Central de Reclamações — lógica pura.
 *
 * Porte fiel do artifact `central-de-reclamacoes.html` (leitura de 08/09/2026).
 * Regras de agrupamento, natureza e classificação de motivo saem de lá sem alteração:
 * mudar qualquer uma delas muda o número que a Mariana já validou.
 *
 * Fonte dos dados: `/data/central-reclamacoes-ml.json` e `/data/central-reclamacoes-shopee.json`,
 * gerados por `fiesta_party_dados/FIESTA PARTY DADOS/scripts/reclamacoes/extrair_do_artifact.py`.
 */

// ---------------------------------------------------------------- tipos

export type ContaMl = 'F' | 'H'

export type CasoMl = {
  id: string
  conta: ContaMl
  motivoCod: string
  motivoLabel: string
  /** motivo que o ML trata como válido e não excluível — não existe texto que reverta */
  hot: boolean
  data: string
  aberta: boolean
  /** o mediador escreveu um prazo ("até [dia] às 14h") */
  temPrazo: boolean
  /** 1 respondeu dentro, 0 perdeu a janela, -1 não se aplica */
  noPrazo: number
  mensagens: number
  /** textos das mensagens quando a fonte bridge as traz (E.1); a contagem continua em mensagens */
  mensagensDetalhe?: Array<{ de?: string; em?: string | null; texto?: string }>
  mlb: string | null
  itemTitulo: string | null
  valor: number | null
  /** desfecho aplicado, quando o caso já encerrou (só vem do histórico) */
  solucao?: { aplicada: boolean; label: string | null; tipo: string | null } | null
}

export type ItemMl = { mlb: string; titulo: string }

export type DadosMl = {
  meta: {
    fonte: string
    coletado_em: string
    periodo: { de: string; ate: string }
    contas: Record<string, string>
    is_sample?: boolean
  }
  itens: ItemMl[]
  casos: CasoMl[]
}

export type LojaShopee = 'FESTA' | 'DECORA'

export type MotivoShopee = {
  cod: string
  label: string
  festa: { total: number; canceladas: number; valor: number }
  decora: { total: number; canceladas: number; valor: number }
}

export type FilaShopee = {
  loja: LojaShopee
  lojaCod: 'L1' | 'L2'
  sn: string
  status: string
  motivoCod: string
  valor: number
  data: string
  /** esperando resposta do vendedor */
  pendente: boolean
  produto: string
  texto: string
}

export type DadosShopee = {
  meta: {
    fonte: string
    coletado_em: string
    periodo: { de: string; ate: string }
    totais_declarados: { devolucoes: number; valor: number; festa: number; decora: number }
  }
  serieDiaria: Array<{ dia: string; festa: number; decora: number }>
  motivos: MotivoShopee[]
  fila: FilaShopee[]
}

// ---------------------------------------------------------------- ML: classificação

/** Famílias de produto, pela palavra que manda no título (ordem importa). */
const FAMILIAS: Array<[string, RegExp]> = [
  [
    'Iluminação e máquina',
    /strobo|fuma[çc]a|refletor|globo espelhado|projetor|bolha de sab[ãa]o|moving|canh[ãa]o|vela de led|m[áa]quina de/i,
  ],
  ['Placa e piso adesivo', /placa|piso |m[áa]rmore|tijolinho|shimmer|painel/i],
  [
    'Kit descartáveis',
    /kit descart|kit festa|kit pratinho|kit talher|kit churrasco|kit copos|kit garfo|kit sobremesa|kit copinho|kit decora|descart[áa]veis festa|festa de anivers[áa]rio descart/i,
  ],
  ['Balão', /bal[aã]o|bal[õo]es|bexiga|bubble/i],
  ['Copo descartável', /copo|copinho/i],
  [
    'Talher, prato e outros',
    /garfo|colher|prato|faca|forminha|topo|potinho|molheira|decora[çc][ãa]o/i,
  ],
]

export const familiaDe = (titulo: string): string => {
  for (const [nome, re] of FAMILIAS) if (re.test(titulo)) return nome
  return 'Outros'
}

/** Natureza da reclamação, para o ranking de anúncios. */
const CHEGOU = new Set(['PDD9952', 'PDD9955', 'PDD9946', 'PDD9944', 'PDD9949', 'PDD9941', 'PDD9942'])
const ARREP = new Set(['PDD9939', 'PNR9501', 'PNR9509'])
const ENTREGA = new Set([
  'PNR9508',
  'PNR9513',
  'PNR9504',
  'PNR9510',
  'PNR9511',
  'PNR9505',
  'PNR9560',
  'PNR9567',
])

export type Natureza = 'g1' | 'g2' | 'g3' | 'g4'

export const NATUREZA_LABEL: Record<Natureza, string> = {
  g1: 'chegou e deu problema',
  g2: 'arrependimento',
  g3: 'entrega e endereço',
  g4: 'outros',
}

export const naturezaDe = (motivoCod: string): Natureza =>
  CHEGOU.has(motivoCod) ? 'g1' : ARREP.has(motivoCod) ? 'g2' : ENTREGA.has(motivoCod) ? 'g3' : 'g4'

/** Tipos usados na matriz anúncio × tipo. */
export const TIPOS = [
  'Item faltando',
  'Chegou quebrado',
  'Cor/tamanho/modelo errado',
  'Descumpre o anúncio',
  'Recebi outro produto',
  'Não funciona',
  'Arrependimento',
  'Arrep. antes da entrega',
  'Não entregue',
  'Não posso esperar',
  'Diz não ter recebido',
  'Endereço',
  'Outros',
] as const

const LABEL_PARA_TIPO: Record<string, string> = {
  'Arrependimento — chegou bem, não quero mais': 'Arrependimento',
  'Arrependimento antes da entrega': 'Arrep. antes da entrega',
  'Item faltando na caixa': 'Item faltando',
  'Não entregue — outro motivo': 'Não entregue',
  'Cor, tamanho ou modelo errado': 'Cor/tamanho/modelo errado',
  'Chegou quebrado': 'Chegou quebrado',
  'Descumpre o anúncio': 'Descumpre o anúncio',
  'Não posso esperar a entrega': 'Não posso esperar',
  'Entregue, mas diz não ter recebido': 'Diz não ter recebido',
  'Endereço de entrega': 'Endereço',
  'Recebi outro produto': 'Recebi outro produto',
  'Não funciona': 'Não funciona',
}

export const tipoDe = (motivoLabel: string): string => LABEL_PARA_TIPO[motivoLabel] || 'Outros'

/** Cabeçalho curto da matriz — os nomes inteiros não cabem na coluna. */
export const TIPO_CURTO: Record<string, string> = {
  'Cor/tamanho/modelo errado': 'Cor ou tamanho',
  'Arrep. antes da entrega': 'Arrep. antes de chegar',
  'Diz não ter recebido': 'Diz que não recebeu',
  'Descumpre o anúncio': 'Fora do anúncio',
}

/** Item faltando na caixa — os dois códigos que o painel conta como tal. */
const ITEM_FALTANDO = new Set(['PDD9952', 'PDD9955'])

// ---------------------------------------------------------------- ML: filtro e agregações

export type FiltroMl = {
  de: string
  ate: string
  conta: ContaMl | 'all'
  motivo: string | 'all'
}

export const filtrarCasos = (casos: CasoMl[], f: FiltroMl): CasoMl[] =>
  casos.filter(
    (c) =>
      c.data >= f.de &&
      c.data <= f.ate &&
      (f.conta === 'all' || c.conta === f.conta) &&
      (f.motivo === 'all' || c.motivoLabel === f.motivo),
  )

export type KpisMl = {
  total: number
  abertas: number
  comPrazo: number
  /** null quando nenhum caso do recorte teve prazo escrito */
  pctNoPrazo: number | null
  perdidas: number
  itemFaltando: number
}

export const kpisMl = (casos: CasoMl[]): KpisMl => {
  const comPrazo = casos.filter((c) => c.temPrazo)
  const ok = comPrazo.filter((c) => c.noPrazo === 1).length
  return {
    total: casos.length,
    abertas: casos.filter((c) => c.aberta).length,
    comPrazo: comPrazo.length,
    pctNoPrazo: comPrazo.length ? Math.round((ok / comPrazo.length) * 100) : null,
    perdidas: comPrazo.filter((c) => c.noPrazo === 0).length,
    itemFaltando: casos.filter((c) => ITEM_FALTANDO.has(c.motivoCod)).length,
  }
}

export type BarraMotivo = { label: string; n: number; pct: number; hot: boolean }

export const motivosAgrupados = (casos: CasoMl[]): BarraMotivo[] => {
  const agg = new Map<string, { n: number; hot: boolean }>()
  for (const c of casos) {
    const k = c.motivoLabel || 'Outros'
    const cur = agg.get(k) || { n: 0, hot: false }
    cur.n += 1
    if (c.hot) cur.hot = true
    agg.set(k, cur)
  }
  return [...agg.entries()]
    .map(([label, v]) => ({
      label,
      n: v.n,
      pct: casos.length ? Math.round((v.n / casos.length) * 100) : 0,
      hot: v.hot,
    }))
    .sort((a, b) => b.n - a.n)
}

/** Só os casos em que o mediador escreveu prazo. Perdidas primeiro, depois mais recentes. */
export const janelasDePrazo = (casos: CasoMl[]): CasoMl[] =>
  casos
    .filter((c) => c.temPrazo)
    .slice()
    .sort((a, b) => a.noPrazo - b.noPrazo || (a.data < b.data ? 1 : -1))

export type AgregadoItem = {
  mlb: string
  titulo: string
  n: number
  valor: number
  natureza: Record<Natureza, number>
  tipos: Record<string, number>
  contas: Record<string, number>
}

export type AgregadoFamilia = {
  nome: string
  n: number
  valor: number
  natureza: Record<Natureza, number>
  anuncios: number
}

const zeroNat = (): Record<Natureza, number> => ({ g1: 0, g2: 0, g3: 0, g4: 0 })

export const agregarPorItem = (
  casos: CasoMl[],
): { ranking: AgregadoItem[]; familias: AgregadoFamilia[] } => {
  const porItem = new Map<string, AgregadoItem>()
  const porFamilia = new Map<string, AgregadoFamilia & { itens: Set<string> }>()

  for (const c of casos) {
    if (!c.mlb || !c.itemTitulo) continue
    const valor = c.valor || 0
    const g = naturezaDe(c.motivoCod)
    const tp = tipoDe(c.motivoLabel)

    let item = porItem.get(c.mlb)
    if (!item) {
      item = {
        mlb: c.mlb,
        titulo: c.itemTitulo,
        n: 0,
        valor: 0,
        natureza: zeroNat(),
        tipos: {},
        contas: {},
      }
      porItem.set(c.mlb, item)
    }
    item.n += 1
    item.valor += valor
    item.natureza[g] += 1
    item.tipos[tp] = (item.tipos[tp] || 0) + 1
    item.contas[c.conta] = (item.contas[c.conta] || 0) + 1

    const nome = familiaDe(c.itemTitulo)
    let fam = porFamilia.get(nome)
    if (!fam) {
      fam = { nome, n: 0, valor: 0, natureza: zeroNat(), anuncios: 0, itens: new Set() }
      porFamilia.set(nome, fam)
    }
    fam.n += 1
    fam.valor += valor
    fam.natureza[g] += 1
    fam.itens.add(c.mlb)
  }

  const ranking = [...porItem.values()].sort((a, b) => b.n - a.n || b.valor - a.valor)
  const familias = [...porFamilia.values()]
    .map(({ itens, ...f }) => ({ ...f, anuncios: itens.size }))
    .sort((a, b) => b.n - a.n)

  return { ranking, familias }
}

export type Matriz = {
  colunas: string[]
  linhas: Array<{ item: AgregadoItem; celulas: number[]; total: number }>
  max: number
}

/** Os N anúncios mais reclamados × os tipos que de fato aparecem no recorte. */
export const matrizAnuncioTipo = (ranking: AgregadoItem[], quantos = 12): Matriz => {
  const top = ranking.slice(0, quantos)
  const colunas = TIPOS.filter((t) => top.some((o) => o.tipos[t]))
  const max = Math.max(1, ...top.flatMap((o) => colunas.map((c) => o.tipos[c] || 0)))
  return {
    colunas: [...colunas],
    linhas: top.map((item) => ({
      item,
      celulas: colunas.map((c) => item.tipos[c] || 0),
      total: item.n,
    })),
    max,
  }
}

export type SolucaoAgregada = {
  label: string
  n: number
  pct: number
  valor: number
}

/**
 * Soluções aplicadas no período.
 *
 * Conta só o que teve desfecho de verdade. Caso que morreu sozinho (a Shopee
 * cancela um terço da fila por inatividade) fica fora de `aplicadas` — creditar
 * isso à equipe seria contar trabalho que não houve.
 */
export const solucoesAplicadas = (
  casos: CasoMl[],
): { aplicadas: SolucaoAgregada[]; semDesfecho: number; total: number } => {
  const agg = new Map<string, { n: number; valor: number }>()
  let comDesfecho = 0

  for (const c of casos) {
    if (!c.solucao?.aplicada) continue
    comDesfecho += 1
    const label = c.solucao.label || c.solucao.tipo || 'Desfecho não identificado'
    const atual = agg.get(label) || { n: 0, valor: 0 }
    atual.n += 1
    atual.valor += c.valor || 0
    agg.set(label, atual)
  }

  return {
    aplicadas: [...agg.entries()]
      .map(([label, v]) => ({
        label,
        n: v.n,
        valor: v.valor,
        pct: comDesfecho ? Math.round((v.n / comDesfecho) * 100) : 0,
      }))
      .sort((a, b) => b.n - a.n),
    semDesfecho: casos.length - comDesfecho,
    total: casos.length,
  }
}

/** Lista de motivos para o seletor — todos os que existem na base, não só no recorte. */
export const motivosDisponiveis = (casos: CasoMl[]): string[] =>
  [...new Set(casos.map((c) => c.motivoLabel || 'Outros'))].sort()

// ---------------------------------------------------------------- Shopee

export type FiltroLojaShopee = 'all' | 'L1' | 'L2'

export type BarraMotivoShopee = {
  cod: string
  label: string
  total: number
  canceladas: number
  mantidas: number
  valor: number
  pctCanceladas: number
}

export const motivosShopee = (
  motivos: MotivoShopee[],
  loja: FiltroLojaShopee,
): BarraMotivoShopee[] =>
  motivos
    .map((m) => {
      const total = (loja === 'L2' ? 0 : m.festa.total) + (loja === 'L1' ? 0 : m.decora.total)
      const canceladas =
        (loja === 'L2' ? 0 : m.festa.canceladas) + (loja === 'L1' ? 0 : m.decora.canceladas)
      const valor = (loja === 'L2' ? 0 : m.festa.valor) + (loja === 'L1' ? 0 : m.decora.valor)
      return {
        cod: m.cod,
        label: m.label,
        total,
        canceladas,
        mantidas: total - canceladas,
        valor,
        pctCanceladas: total ? Math.round((canceladas / total) * 100) : 0,
      }
    })
    .filter((m) => m.total > 0)
    .sort((a, b) => b.total - a.total)

export type FiltroFilaShopee = 'all' | 'hot' | 'L1' | 'L2'

export const filaShopee = (fila: FilaShopee[], f: FiltroFilaShopee): FilaShopee[] =>
  fila.filter((r) => {
    if (f === 'hot') return r.pendente
    if (f === 'L1' || f === 'L2') return r.lojaCod === f
    return true
  })

/** Rótulos da situação da devolução, como a Central do Vendedor mostra. */
export const SITUACAO_SHOPEE: Record<string, { label: string; tom: 'neutro' | 'warn' | 'crit' }> = {
  PROCESSING: { label: 'em análise', tom: 'neutro' },
  JUDGING: { label: 'em julgamento', tom: 'warn' },
  REQUESTED: { label: 'solicitada', tom: 'warn' },
  SELLER_DISPUTE: { label: 'disputa aberta', tom: 'crit' },
}

export const MOTIVO_SHOPEE_CURTO: Record<string, string> = {
  WRONG_ITEM: 'Produto errado',
  CHANGE_MIND: 'Arrependimento',
  NOT_RECEIPT: 'Não recebeu',
  ITEM_MISSING: 'Faltou item',
  FUNCTIONAL_DMG: 'Defeito',
  DAMAGED_OTHERS: 'Avaria',
  SUSPICIOUS_PARCEL: 'Pacote suspeito',
}

// ---------------------------------------------------------------- conteúdo editorial

export type Leitura = {
  ordem: number
  prioridade: number
  titulo: string
  why: string
  paras: string[]
}

export type RegraChat = {
  destaque: boolean
  titulo: string
  why: string
  paras: string[]
  lados: Array<{ lado: 'yes' | 'no'; titulo: string; texto: string }>
  modelos: Array<{ label: string; texto: string }>
}

export type BlocoShopee =
  | { t: 'note'; tom: 'good' | 'crit' | 'neutro'; titulo: string; texto: string }
  | { t: 'thesis'; claim: string; paras: string[] }
  | {
      t: 'stores'
      lojas: Array<{
        nome: string
        shopId: string
        nota: string
        metricas: { head: string[]; rows: string[][]; tons: string[] } | null
      }>
    }
  | { t: 'kpis'; itens: Array<{ valor: string; label: string; sub: string }> }
  | {
      t: 'quotes'
      colunas: Array<{
        titulo: string
        sub: string
        falas: Array<{ texto: string; cite: string }>
        fecho: string
      }>
    }
  | { t: 'rulegrid'; cards: Array<{ titulo: string; itens: string[]; paras: string[] }> }
  | { t: 'steps'; passos: Array<{ n: string; titulo: string; paras: string[] }> }
  | { t: 'plan'; acoes: Array<{ titulo: string; paras: string[]; why: string; tags: string[] }> }
  | { t: 'slot'; id: string }
  | { t: string; titulo?: string; paras?: string[]; texto?: string }

export type Conteudo = {
  ml: {
    header: { eyebrow: string; titulo: string; sub: string }
    secheads: string[]
    leituras: Leitura[]
    regrasChat: RegraChat[]
    fonte: string
  }
  shopee: {
    header: {
      eyebrow: string
      titulo: string
      lead: string
      metaline: string[]
      aviso: { titulo: string; texto: string } | null
    }
    secoes: Array<{ titulo: string; count: string; blocos: BlocoShopee[] }>
  }
}

// ---------------------------------------------------------------- formatação

export const brl0 = (n: number): string =>
  `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`

export const brl2 = (n: number): string =>
  `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export const diaCurto = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`

export const NOME_CONTA_ML: Record<ContaMl, string> = {
  F: 'FESTA',
  H: 'HOME',
}
